<template>
  <v-layout>
    <div v-if="loading">
      <spinner />
    </div>
    <v-layout v-else column>
      <v-flex class="pb-16">
        <text-field
          v-model.trim="form.name"
          autofocus
          :validator="vs.form.name"
          placeholder="e.g. VIP Ticket, Regular, Early Bird, etc."
          :error-messages="{
            maxLength: 'Name can’t be more than 170 characters',
          }"
          label="Ticket name*"
        />
      </v-flex>
      <div class="pb-16" style="display: flex">
        <v-select
          v-model="form.category"
          box
          label="Ticket category"
          :items="categories"
          style="flex: 7"
          hint="Used for ticket segmentation. We recommend more than 1 category."
          persistent-hint
          :disabled="categories.length === 0"
        >
          <template v-slot:selection="{ item }">
            <div class="color" :style="{ background: item.hexadecimal }" />
            {{ item.name }}
          </template>
          <template v-slot:item="{ item }">
            <div class="color" :style="{ background: item.hexadecimal }" />
            {{ item.name }}
          </template>
        </v-select>
        <main-button flat @click.prevent="handleOrganizationItemCategoryModalState">
          Create category
        </main-button>
      </div>
      <v-layout class="pb-8">
        <text-field
          v-model.trim="form.price"
          type="number"
          step="0.01"
          :disabled="isSoldOut"
          :suffix="currency"
          :onwheel="true"
          :validator="vs.form.price"
          :error-messages="{
            minValue: 'Price must be higher than 0.',
          }"
          label="Price*"
        />
      </v-layout>
      <p v-if="isSoldOut" class="price-disabled-description">
        Price cannot be changed after a ticket has been sold at least once.
      </p>
      <div
        v-if="
          [TicketType.TABLE, TicketType.PARTER].includes(ticketType) ||
          !event.extraModules.floorPlan
        "
        class="pb-16"
        style="display: flex; gap: 30px"
      >
        <text-field
          v-model.number="form.itemMeta.min"
          type="number"
          step="1"
          :validator="vs.form.itemMeta.min"
          :error-messages="{
            minValue: 'Quantity must be higher than 0.',
          }"
          label="Per order min*"
          hint="Minimum of tickets a customer must choose when purchasing."
          persistent-hint
        />
        <text-field
          v-model.number="form.itemMeta.max"
          type="number"
          step="1"
          :validator="vs.form.itemMeta.max"
          :error-messages="{
            minValue: 'Quantity must be higher than 0.',
          }"
          label="Per order max*"
          :hint="itemMetaMaxHint"
          persistent-hint
        />
      </div>
      <v-flex
        v-if="!event.extraModules.floorPlan || ticketType === TicketType.PARTER"
        class="pb-16"
      >
        <text-field
          v-model.number="form.initialQuantity"
          type="number"
          :validator="vs.form.initialQuantity"
          label="Initial quantity*"
          :onwheel="true"
          hint="Amount of tickets that will be available for sale."
          persistent-hint
          :error-messages="{
            maxValue:
              maxInitialQuantity === 0
                ? 'You cannot add any new tickets because total tickets number already exceeded event capacity.'
                : `Total quantity exceeds event capacity. You can set the quantity to a maximum of ${maxInitialQuantity}.`,
            minValue: 'Quantity must be higher than 0.',
            validateInitialQuantity: 'Quantity must be bigger than number of already sold tickets!',
          }"
        />
      </v-flex>
      <v-layout class="pb-8">
        <v-flex class="pr-30" xs12>
          <datetime-field
            v-model="form.from"
            :validator="vs.form.from"
            label="Start date*"
            :min="new Date(new Date().getTime()).toString()"
            :max="form.to ? form.to.toString() : null"
            hint="Start date of sale of this ticket type. To start sale immediately, set it to current time."
            :error-messages="{
              minDate: 'Start date must be a future date',
              maxDate: 'Start date should be before the end of the event',
            }"
          />
        </v-flex>
        <v-flex xs12>
          <datetime-field
            v-model="form.to"
            :validator="vs.form.to"
            label="End date*"
            hint="End date of sale of this ticket type."
            :min="
              form.from
                ? form.from.toString()
                : new Date(new Date().getTime() + 1000 * 60 * 5).toString()
            "
            :error-messages="{
              minDate: 'End date should not be before the start of the event',
            }"
          />
        </v-flex>
      </v-layout>
      <checkbox
        v-model="form.hasDateValidationRange"
        class="pb-16"
        :validator="vs.form.hasDateValidationRange"
        :color="'#0277BD'"
        hint="Enable to limit the time of scanning the entrance to the event (via CoreEvent Scanner app).
Example – festival tickets for Friday and Saturday. Enabling this allows Friday/Saturday tickets to be scanned only on that day."
      >
        <div>Entry with this ticket is restricted by date range</div>
      </checkbox>
      <v-flex class="pb-16">
        <textarea-field
          v-model.trim="form.description"
          :validator="vs.form.description"
          label="Ticket description"
          hint="Ticket description visible to customers when buying a ticket."
        />
      </v-flex>
      <v-layout v-if="form.hasDateValidationRange" class="pb-8">
        <v-flex class="pr-30" xs12>
          <datetime-field
            v-model="form.entryFrom"
            :validator="vs.form.entryFrom"
            label="Entry from date"
          />
        </v-flex>
        <v-flex xs12>
          <datetime-field
            v-model="form.entryTo"
            :validator="vs.form.entryTo"
            label="Entry to date"
          />
        </v-flex>
      </v-layout>
      <div v-if="!event.extraModules.floorPlan">
        <v-flex class="pb-16">
          <select-field
            v-model="selectedSalesChannel"
            :disabled="!form.price || Number(form.price) === 0"
            :items="salesChannels"
            label="Sales Channel"
            hint="Besides the existing online channel, select CoreEvent physical locations that you want to use for sales."
          />
        </v-flex>
        <v-flex class="pb-16">
          <p v-if="Number(form.price) === 0" class="tisak-term pt-2">
            The price of the ticket must be greater than 1 euro to be able to add ticket sellers.
          </p>
        </v-flex>
        <v-flex class="pb-16">
          <v-select
            v-if="
              selectedSalesChannel === SalesChannels.SPECIFIC_OUTLETS ||
              selectedSalesChannel === SalesChannels.ALL_OUTLETS
            "
            v-model="form.sellers"
            :items="availableSellers"
            :menu-props="{ offsetY: true }"
            chips
            hide-selected
            item-text="name"
            item-value="id"
            label="Select Outlets"
            hint="TISAK outlets will charge ticket buyers additional 0,53 EUR per ticket"
            persistent-hint
            multiple
          >
            <template v-slot:selection="{ item }">
              <v-chip color="white" text-color="#0277BD" close @input="deselectSeller(item)">
                {{ item.name }}
              </v-chip>
            </template>
          </v-select>
          <p
            v-if="tisak && form.sellers && form.sellers.length && form.sellers.includes(tisak.id)"
            class="tisak-term"
          >
            TISAK outlets will charge ticket buyers additional 1 EUR per ticket
          </p>
          <checkbox v-model="form.showIfSold" :color="'#0277BD'">
            <div>Display the ticket in the store if sold out or has expired</div>
          </checkbox>
        </v-flex>
      </div>
      <manage-ticket-categories-modal
        v-if="openModal"
        :show.sync="openModal"
        :initial-values="organizationItemCategory"
        :colors="colors"
        :priorities="priorities"
        :categories="categories"
        @values="organizationItemCategory = $event"
        @close="handleOrganizationItemCategoryModalState"
        @create-category="handleCreateCategory($event)"
      />
    </v-layout>
    <simple-snackbar v-bind.sync="snackbar" />
  </v-layout>
</template>

<script>
import { GET_TICKET_SELLERS } from '@/graphql/user.graphql'
import { exchangeRate, tisakTicketSeller } from '@/utils'
import { SalesChannels, TicketType } from '@/types'
import {
  CREATE_ORGANIZATION_ITEM_CATEGORY,
  GET_ORGANIZATION_ITEM_CATEGORIES,
} from '@/graphql/organizationItemCategory.graphql'
import ManageTicketCategoriesModal from '@/components/organizations/ticket-categories/ManageTicketCategoriesModal.vue'
import {
  CategoryColors,
  Priorities,
} from '@/components/organizations/ticket-categories/TicketsCategoriesContainer.vue'
import { required } from 'vuelidate/lib/validators'
import simpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'

export default {
  name: 'ticket-form',
  components: { simpleSnackbar, ManageTicketCategoriesModal },
  props: {
    initialValues: {
      type: Object,
      default: null,
    },
    vs: {
      type: Object,
      default: () => ({}),
    },
    eventId: {
      default: null,
      type: String,
    },
    event: {
      default: null,
      type: Object,
    },
    currency: {
      default: null,
      type: String,
    },
    isSoldOut: {
      default: false,
      type: Boolean,
    },
    organizationSlug: {
      default: null,
      type: String,
    },
    ticketType: {
      type: String,
      default: TicketType.REGULAR,
    },
    maxInitialQuantity: null,
  },
  data() {
    return {
      form: {},
      promoCode: {},
      exchangeRate: exchangeRate,
      promoCodeExists: false,
      loading: true,
      inEdit: false,
      SalesChannels,
      salesChannels: Object.values(SalesChannels),
      selectedSalesChannel: SalesChannels.ONLINE,
      availableSellers: [],
      tisak: {},
      categories: [],
      TicketType,
      openModal: false,
      colors: CategoryColors,
      snackbar: {},
      priorities: Priorities.map(p => p.roman),
      organizationItemCategory: {},
    }
  },
  computed: {
    itemMetaMaxHint() {
      let text = 'Maximum of tickets a customer can choose when purchasing.'
      if (this.ticketType !== TicketType.TABLE) {
        text += ' Maximum limit can be set to 10.'
      }

      return text
    },
  },
  watch: {
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
    availableSellers: {
      handler(availableSellers) {
        this.determineInitialSelectedSalesChannel(availableSellers)
      },
    },
    selectedSalesChannel: {
      handler(_) {
        this.determineInitialSelectedSellers()
      },
    },
  },
  mounted() {
    this.form = this.initialValues || { sellers: [] }
    if (this.initialValues) {
      this.inEdit = true
    }
    this.loading = false
  },
  validations() {
    return {
      organizationItemCategory: {
        name: { required },
        color: { required },
        priority: { required },
      },
    }
  },
  apollo: {
    getTicketSellers: {
      query: GET_TICKET_SELLERS,
      result({ data, loading }) {
        if (!loading) {
          const { getTicketSellers: users } = data
          this.availableSellers = users
          this.tisak = users.find(u => u.name.toLowerCase() === tisakTicketSeller)
        }
      },
    },
    getCategories: {
      query: GET_ORGANIZATION_ITEM_CATEGORIES,
      manual: true,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          pagination: {
            page: 0,
            per_page: -1,
          },
        }
      },
      result({ data: { getOrganizationItemCategories }, loading }) {
        if (!loading) {
          this.categories = getOrganizationItemCategories.categories.map(category => {
            return {
              id: category.id,
              name: category.name,
              hexadecimal: category.color,
              priority: category.priority,
            }
          })
          if (this.initialValues) {
            this.form.category = this.categories.find(
              c => c.id === this.initialValues.organizationItemCategoryId,
            )
          }
        }
      },
    },
  },
  methods: {
    handleOrganizationItemCategoryModalState() {
      this.organizationItemCategory = {}
      this.openModal = !this.openModal
    },
    savePromo() {
      this.promoCodeExists = true
      this.$emit('promoValue', this.promoCode)
    },
    deselectSeller(item) {
      this.form.sellers = this.form.sellers.filter(sellerId => sellerId !== item.id)
    },
    determineInitialSelectedSalesChannel(availableSellers) {
      if (this.initialValues.sellers && this.initialValues.sellers.length) {
        if (this.initialValues.sellers.length === availableSellers.length) {
          this.selectedSalesChannel = this.SalesChannels.ALL_OUTLETS
        } else {
          this.selectedSalesChannel = this.SalesChannels.SPECIFIC_OUTLETS
        }
      } else {
        this.selectedSalesChannel = this.SalesChannels.ONLINE
      }
    },
    determineInitialSelectedSellers() {
      switch (this.selectedSalesChannel) {
        case this.SalesChannels.ALL_OUTLETS:
          this.form.sellers = this.availableSellers.map(seller => seller.id)
          break
        case this.SalesChannels.SPECIFIC_OUTLETS:
          this.form.sellers = this.initialValues
            ? this.initialValues.sellers
            : this.form.sellers || []
          break
        default:
          this.form.sellers = []
      }
    },
    getDecimalFromRomanNumber(roman) {
      for (const number of Priorities) {
        if (number.roman === roman.toUpperCase()) return number.decimal
      }

      return 0
    },
    async handleCreateCategory(organizationItemCategory) {
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_ITEM_CATEGORY,
        awaitRefetchQueries: true,
        refetchQueries: ['GetOrganizationItemCategories'],
        variables: {
          organizationSlug: this.organizationSlug,
          ...organizationItemCategory,
          color: organizationItemCategory.color.hexadecimal,
          priority: this.getDecimalFromRomanNumber(organizationItemCategory.priority),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Category successfully created!',
          color: 'success',
        }
        this.openModal = false
        this.$v.$reset()
        this.organizationItemCategory = {}
      }
    },
  },
}
</script>

<style scoped>
@media (max-width: 800px) {
  .pb-8 {
    margin-bottom: 1rem;
  }

  .pr-30,
  .pr-30 + * {
    margin-right: 0 !important;
  }
}

.pr-30 {
  margin-bottom: 1rem;
}

.price-disabled-description {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #666666;
  margin-bottom: 1rem;
}

.tisak-term {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #666666;
  text-indent: 1rem;
  margin-top: -0.75rem;
}

.category-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.color {
  width: 12px;
  height: 12px;
  border-radius: 10000px;
  margin-right: 0.5rem;
}
</style>
